// src/pages/Creatives.jsx
import React from 'react';
import { Box, Typography, Button, Container, Grid, Card, CardContent, Link } from '@mui/material';

// const brandYellow = '#FCCB26';

const Section = ({ title, children, bg, color }) => (
    <Box py={8} bgcolor={bg || 'white'} color={color || 'black'}>
        <Container>
            {title && (
                <Typography variant="h4" fontWeight="bold" mb={4} textAlign="center">
                    {title}
                </Typography>
            )}
            {children}
        </Container>
    </Box>
);

// #787777
// #a3a2a2
// #c0bebe
// #e5e3e3

const Studios = () => {
    return (
        <Box>

            {/* Hero Section with Background Image and Blur */}
            <Box
                sx={{
                    position: 'relative',
                    height: '95vh',
                    color: 'white',
                    overflow: 'hidden',
                }}
            >
                {/* Background Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'url(/assets/servicesPic/studios.jpg)', // ← Replace with your actual image
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'brightness(0.6) blur(1px)',
                        zIndex: 0,
                    }}
                />

                {/* Content Over Background */}
                <Box
                    sx={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        zIndex: 1,
                        textAlign: 'center',
                        px: 3,
                    }}
                >
                    <Typography variant="h2" fontWeight="bold" mb={2}>
                        🎥 Higher Low for Studios & High-Tier Productions
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#FCCB26' }} gutterBottom>
                        Scalable Production. Proven Talent. Creative Alignment.
                    </Typography>
                    <Typography variant="body1" maxWidth="md" fontSize={20}>
                        A System That Integrates Seamlessly. Higher Low works as a fully-equipped creative arm or a specialized partner that integrates directly into existing studio pipelines. Whether you need a trusted team to execute a standalone production or a department to fill in the gaps, we bring the structure and talent to deliver at scale — with intention.
                    </Typography>
                </Box>
            </Box>



            {/* What We Do Section */}
            <Section title="What We Offer Studios & Productions" bg="#e5e3e3">
                <Grid container spacing={4}>
                    {[
                        ['End-to-End Creative Production', 'Commercials, episodic content, documentaries, branded films — fully managed or in partnership with your leads.'],
                        ['Specialized Department Support', 'Cinematography, directing, sound, editing, color — tailored crews that integrate cleanly into your workflow.'],
                        ['Pitch Decks & Concept Development', 'From sizzle reels to visual bibles and treatments — we help develop strong, clear material for buyers and platforms.'],
                        ['Festival & Platform Strategy', 'For IPs with prestige or long-term goals, we offer guidance on positioning and visibility with festivals and streaming platforms.'],
                        ['On-Call Creative Infrastructure', 'Access to our insured, reliable crew network and gear, with rapid deployment when timelines are tight.']
                    ]
                        .map(([title, desc], idx) => (
                            <Grid item xs={12} md={6} key={idx}>
                                <Box p={3} bgcolor="#787777" borderRadius={2} boxShadow={2}>
                                    <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                    <Typography variant="body2">{desc}</Typography>
                                </Box>
                            </Grid>
                        ))}
                </Grid>
            </Section>

            {/* How It Works Section */}
            <Section title="How It Works" bg="#a3a2a2">
                <Grid container spacing={4}>
                    {[
                        ['Tell Us the Scope', 'Whether you’re building from scratch or need reliable hands to join in — we start with your production goals.'],
                        ['Plug In or Build Out', 'We can take the lead on execution or provide exact-fit departments or services to fill in your needs.'],
                        ['Deliver Cleanly', "Transparent communication, streamlined billing, and clear deliverables — handled professionally end to end."]
                    ].map(([title, desc], idx) => (
                        <Grid item xs={12} md={4} key={idx}>
                            <Box p={2} bgcolor="#c0bebe" borderRadius={2} boxShadow={1}>
                                <Typography variant="h6" fontWeight="bold">{title}</Typography>
                                <Typography variant="body2">{desc}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Why Higher Low Section (Improved) */}
            <Section title="Why Higher Low?" bg="#c0bebe">
                <Grid container spacing={3}>
                    {[
                        'Streamlined, professional pipeline with creative flexibility',
                        'Access to diverse, high-caliber crew and department leads',
                        'Scalable infrastructure for quick turnarounds',
                        'Clean, insured, and trusted — no ego, no friction',
                        'Deep commitment to vision, aesthetics, and storytelling'
                    ].map((text, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card elevation={7} sx={{ height: '100%', borderRadius: 2, backgroundColor: '#a3a2a2' }}>
                                <CardContent>
                                    <Typography variant="body1" fontWeight="medium">
                                        {text}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Section>

            {/* Call To Action Section (Centered + Popped) */}
            <Section bg="#e5e3e3">
                <Box textAlign="center">
                    <Typography variant="h4" fontWeight="bold" mb={3}>
                        Start Something
                    </Typography>
                    <Typography variant="h6" mb={4}>
                        If you’re ready to make, grow, or test something creatively — we’re here.
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={3} flexWrap="wrap" mb={4}>
                        <Button
                            component="a"
                            href="mailto:shahrooz@higherlowfilms.com?subject=Project Brief Submission"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            size="large"
                            sx={{
                                bgcolor: '#787777',
                                color: 'black',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 4,
                                '&:hover': { bgcolor: '#a3a2a2' },
                            }}
                        >
                            Submit Your Project Scope
                        </Button>

                        <Button
                            component="a"
                            href="https://calendly.com/higherlowfilms/30min"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            size="large"
                            sx={{
                                borderColor: '#787777',
                                color: '#787777',
                                px: 4,
                                py: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                boxShadow: 2,
                                '&:hover': { borderColor: '#a3a2a2', color: '#a3a2a2' },
                            }}
                        >
                            Book a Production Call
                        </Button>
                    </Box>


                    <Typography variant="body1">
                        👀 Need different support? View how we serve{' '}
                        <Link href="/services/brands" underline="hover" fontWeight="bold">
                            Brands
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/creatives" underline="hover" fontWeight="bold">
                            Creatives
                        </Link>{' '}
                        –{' '}
                        <Link href="/services/supports" underline="hover" fontWeight="bold">
                            Supports
                        </Link>{' '}
                        →
                    </Typography>

                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 4,
                        flexWrap: 'wrap',
                        mt: 6,
                        mb: 8, // space from footer
                        px: 2,
                    }}
                >
                    <Box
                        sx={{
                            width: 500,
                            height: 360,
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: 3,
                            backgroundColor: '#eee',
                            backgroundImage: 'url(/assets/servicesPic/studios1.jpg)', // Replace with actual image
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />

                    <Box
                        sx={{
                            width: 500,
                            height: 360,
                            borderRadius: 4,
                            overflow: 'hidden',
                            boxShadow: 3,
                            backgroundColor: '#eee',
                            backgroundImage: 'url(/assets/servicesPic/studios2.jpg)', // Replace with actual image
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                </Box>
            </Section>





        </Box>


    );
};

export default Studios;
